import React, { useEffect, useState } from "react";
import { Key, Copy, Check, Lightning, BookOpen, Cpu, ChartLineUp, ArrowsClockwise, PaperPlaneRight } from "@phosphor-icons/react";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip } from "recharts";
import { toast } from "sonner";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const ENDPOINTS = [
  { m: "GET", p: "/api/v1/models", d: "List every model on the Zeyrodha Universal Key" },
  { m: "POST", p: "/api/v1/chat", d: "Chat / text generation across 10+ models" },
  { m: "POST", p: "/api/v1/image", d: "Image generation (Nano Banana)" },
];

export default function ApiKeys() {
  const [data, setData] = useState(null);
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);
  const [testPrompt, setTestPrompt] = useState("Say hello from the Zeyrodha Universal Key in 5 words");
  const [testOut, setTestOut] = useState("");
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    api.get("/apikey").then((r) => setData(r.data)).catch(() => {});
  }, []);

  const copy = async () => {
    const key = data?.api_key || "";
    try {
      await navigator.clipboard.writeText(key);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = key; document.body.appendChild(ta); ta.select();
      try { document.execCommand("copy"); } catch {}
      document.body.removeChild(ta);
    }
    setCopied(true);
    toast.success("API key copied");
    setTimeout(() => setCopied(false), 1600);
  };

  const rotate = async () => {
    if (!window.confirm("Rotate your Zeyrodha Universal API key? The old key stops working immediately.")) return;
    setRotating(true);
    try {
      const r = await api.post("/apikey/rotate");
      setData((d) => ({ ...d, api_key: r.data.api_key }));
      toast.success("New key generated");
    } catch { toast.error("Could not rotate key"); }
    finally { setRotating(false); }
  };

  const runTest = async () => {
    if (!data?.api_key) return;
    setTesting(true); setTestOut("");
    try {
      const res = await fetch(`${BACKEND}/api/v1/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Zeyrodha-Key": data.api_key },
        body: JSON.stringify({ prompt: testPrompt, model: "gemini-3-flash-preview" }),
      });
      const j = await res.json();
      setTestOut(res.ok ? (j.response || "(empty)") : `Error ${res.status}: ${j.detail || "failed"}`);
      if (res.ok) toast.success("Zeyrodha Universal Key responded");
    } catch (e) { setTestOut("Network error"); }
    finally { setTesting(false); }
  };

  return (
    <div className="p-8 lg:p-10 max-w-6xl">
      <div className="h-16 flex items-center -mt-4 mb-2" />
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-2xl bg-[#FFF4F0] flex items-center justify-center">
          <Key size={24} weight="duotone" className="text-[#F29A86]" />
        </div>
        <div>
          <h1 className="font-display text-3xl font-semibold tracking-tight">Zeyrodha Universal API Key</h1>
          <p className="text-[#594F4D] text-sm">Zeyrodha.AI's own inbuilt key — powers Workspace, App Builder, Agents, Media Studio, TaxOS & GstOS, and your apps</p>
        </div>
      </div>

      {/* Key card */}
      <div className="bg-[#2A2625] text-white rounded-2xl p-6 mt-8 relative overflow-hidden">
        <div className="absolute -top-16 -right-10 w-56 h-56 rounded-full bg-[#FFB8A5]/25 blur-3xl" />
        <div className="relative">
          <p className="text-xs uppercase tracking-wide text-white/50 font-medium">Your Zeyrodha Universal key {data?.is_admin && <span className="ml-2 px-2 py-0.5 rounded-full bg-[#FFB8A5] text-[#2A2625] text-[10px]" data-testid="admin-badge">ADMIN · UNLIMITED</span>}</p>
          <div className="flex items-center gap-3 mt-2">
            <code className="font-mono text-sm bg-white/10 rounded-lg px-4 py-2.5 flex-1 truncate" data-testid="api-key-value">{data?.api_key || "loading…"}</code>
            <button data-testid="copy-key-btn" onClick={copy} className="flex items-center gap-2 bg-[#FFB8A5] text-[#2A2625] font-medium rounded-lg px-4 py-2.5 hover:bg-[#FFCBBF] transition-colors">
              {copied ? <Check size={16} weight="bold" /> : <Copy size={16} />} {copied ? "Copied" : "Copy"}
            </button>
            <button data-testid="rotate-key-btn" onClick={rotate} disabled={rotating} className="flex items-center gap-2 bg-white/10 text-white font-medium rounded-lg px-4 py-2.5 hover:bg-white/20 transition-colors">
              <ArrowsClockwise size={16} className={rotating ? "animate-spin" : ""} /> Rotate
            </button>
          </div>
          <p className="text-xs text-white/50 mt-3">Keep this secret. Send it as <code className="text-white/70">X-Zeyrodha-Key</code> or <code className="text-white/70">Authorization: Bearer</code> to the Zeyrodha Universal API.</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
        {[
          { label: "Total calls", value: data?.total_calls ?? "—", icon: ChartLineUp },
          { label: "Chat calls", value: data?.chat_calls ?? "—", icon: Lightning },
          { label: "Builds", value: data?.build_calls ?? "—", icon: Key },
          { label: "Credits left", value: typeof data?.credits_remaining === "number" ? data.credits_remaining.toLocaleString() : (data?.credits_remaining ?? "—"), icon: ChartLineUp },
        ].map((s) => (
          <div key={s.label} className="bg-white border border-[#2A2625]/10 rounded-2xl p-5">
            <s.icon size={18} weight="duotone" className="text-[#F29A86]" />
            <p className="text-2xl font-display font-semibold mt-2">{s.value}</p>
            <p className="text-xs text-[#8C8381] mt-0.5">{s.label}</p>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-3 gap-5 mt-6">
        {/* Usage chart */}
        <div className="lg:col-span-2 bg-white border border-[#2A2625]/10 rounded-2xl p-6">
          <h3 className="font-display font-semibold mb-4">Usage — last 7 days</h3>
          <div className="h-56" style={{ minHeight: 224 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data?.series || []} margin={{ top: 8, right: 8, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="g" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFB8A5" stopOpacity={0.7} />
                    <stop offset="100%" stopColor="#FFB8A5" stopOpacity={0.05} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="day" tick={{ fontSize: 11, fill: "#8C8381" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: "#8C8381" }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(42,38,37,0.1)", fontSize: 13 }} />
                <Area type="monotone" dataKey="calls" stroke="#F29A86" strokeWidth={2} fill="url(#g)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Docs */}
        <div className="bg-white border border-[#2A2625]/10 rounded-2xl p-6">
          <div className="flex items-center gap-2 mb-4"><BookOpen size={18} weight="duotone" className="text-[#F29A86]" /><h3 className="font-display font-semibold">Endpoints</h3></div>
          <div className="space-y-2.5">
            {ENDPOINTS.map((e) => (
              <div key={e.p} className="text-sm">
                <div className="flex items-center gap-2">
                  <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FFF4F0] text-[#F29A86] font-mono font-semibold">{e.m}</span>
                  <span className="font-mono text-xs">{e.p}</span>
                </div>
                <p className="text-xs text-[#8C8381] mt-0.5">{e.d}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Live tester */}
      <div className="bg-white border border-[#2A2625]/10 rounded-2xl mt-6 p-6" data-testid="api-tester">
        <div className="flex items-center gap-2 mb-3"><PaperPlaneRight size={18} weight="duotone" className="text-[#F29A86]" /><h3 className="font-display font-semibold">Test your key live</h3></div>
        <div className="flex gap-2">
          <input data-testid="api-test-input" value={testPrompt} onChange={(e) => setTestPrompt(e.target.value)} className="flex-1 bg-[#FCFAF8] border border-[#2A2625]/15 rounded-xl px-4 py-2.5 text-sm outline-none focus:ring-2 focus:ring-[#FFB8A5]" />
          <button data-testid="api-test-btn" onClick={runTest} disabled={testing} className="flex items-center gap-2 bg-[#2A2625] text-white font-medium rounded-xl px-5 py-2.5 hover:bg-[#2A2625]/85 transition-colors">{testing ? "Calling…" : "Send"}</button>
        </div>
        {testOut && <pre data-testid="api-test-output" className="mt-3 text-sm bg-[#FCFAF8] border border-[#2A2625]/10 rounded-xl p-4 whitespace-pre-wrap">{testOut}</pre>}
      </div>

      {/* Quickstart */}
      <div className="bg-white border border-[#2A2625]/10 rounded-2xl mt-6 overflow-hidden">
        <div className="px-6 py-4 border-b border-[#2A2625]/10 font-display font-semibold">Quickstart — Zeyrodha Universal API</div>
        <pre className="text-[13px] p-5 overflow-x-auto bg-[#2A2625] text-[#FCE9E2] font-mono">
<code>{`# Chat
curl -X POST "${BACKEND}/api/v1/chat" \\
  -H "X-Zeyrodha-Key: ${data?.api_key || "zk-..."}" \\
  -H "Content-Type: application/json" \\
  -d '{"model":"claude-sonnet-4-6","prompt":"Hello Zeyrodha!"}'

# Image
curl -X POST "${BACKEND}/api/v1/image" \\
  -H "X-Zeyrodha-Key: ${data?.api_key || "zk-..."}" \\
  -H "Content-Type: application/json" \\
  -d '{"prompt":"a peach-colored robot mascot, sticker style"}'

# List models
curl "${BACKEND}/api/v1/models" -H "X-Zeyrodha-Key: ${data?.api_key || "zk-..."}"`}</code>
        </pre>
      </div>
    </div>
  );
}
